// Eigenbau: Fachspezifische Pflege gerichteter Abhängigkeiten aller Planobjekte.
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Link2, Plus, Trash2 } from "lucide-react";
import type { Planobjektart } from "@cubic/shared";
import { Button } from "@/components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { cx, focusRing } from "@/lib/utils";

export type Planreferenz = { art: Planobjektart; id: string };

type KurzesPlanobjekt = Planreferenz & {
  projektId: string;
  kennung: string;
  titel: string;
  erledigt: boolean;
  traegerId?: string;
};

type Abhaengigkeit = {
  id: string;
  voraussetzung: KurzesPlanobjekt;
  abhaengig: KurzesPlanobjekt;
  offen: boolean;
};

const ART_LABEL: Record<Planobjektart, string> = {
  meilenstein: "Meilenstein",
  arbeitspaket: "Arbeitspaket",
  aufgabe: "Aufgabe",
  akzeptanzkriterium: "Akzeptanzkriterium",
};

function PlanobjektVerweis({ objekt }: { objekt: KurzesPlanobjekt }) {
  const inhalt = (
    <>
      <span className="font-mono text-[10.5px] text-tinte4">
        {objekt.kennung}
      </span>
      <span className="truncate text-[13.5px] font-light text-tinte">
        {objekt.titel}
      </span>
    </>
  );
  const klassen = cx(
    "grid min-w-0 flex-1 grid-cols-[auto_1fr] items-center gap-2 hover:text-akzent",
    focusRing,
  );
  if (objekt.art === "aufgabe") {
    return (
      <Link to="/aufgaben/$id" params={{ id: objekt.id }} className={klassen}>
        {inhalt}
      </Link>
    );
  }
  if (objekt.art === "akzeptanzkriterium" && objekt.traegerId) {
    return (
      <Link
        to="/aufgaben/$id"
        params={{ id: objekt.traegerId }}
        className={klassen}
      >
        {inhalt}
      </Link>
    );
  }
  if (objekt.art === "meilenstein") {
    return (
      <Link
        to="/projekte/$projektId/meilensteine/$meilensteinId"
        params={{
          projektId: objekt.projektId,
          meilensteinId: objekt.id,
        }}
        className={klassen}
      >
        {inhalt}
      </Link>
    );
  }
  if (objekt.art === "arbeitspaket") {
    return (
      <Link
        to="/projekte/$projektId/arbeitspakete/$arbeitspaketId"
        params={{
          projektId: objekt.projektId,
          arbeitspaketId: objekt.id,
        }}
        className={klassen}
      >
        {inhalt}
      </Link>
    );
  }
  return <span className={klassen}>{inhalt}</span>;
}

export function Planabhaengigkeiten({
  objekt,
  kannAendern,
  kompakt = false,
}: {
  objekt: Planreferenz;
  kannAendern: boolean;
  kompakt?: boolean;
}) {
  const client = useQueryClient();
  const [offen, setzeOffen] = useState(false);
  const [begriff, setzeBegriff] = useState("");
  const [auswahl, setzeAuswahl] = useState<KurzesPlanobjekt | null>(null);
  const [richtung, setzeRichtung] = useState<"braucht" | "blockiert">(
    "braucht",
  );

  const liste = useQuery({
    queryKey: schluessel.abhaengigkeiten(objekt.art, objekt.id),
    queryFn: () => api.abhaengigkeiten.liste.query(objekt),
  });
  const suche = useQuery({
    queryKey: ["cubic", "planobjekte", "suchen", begriff, objekt.art, objekt.id],
    queryFn: () =>
      api.abhaengigkeiten.suchen.query({
        ...(begriff.trim() ? { begriff: begriff.trim() } : {}),
        grenze: 40,
        ausschliessen: objekt,
      }),
    enabled: offen,
  });

  async function aktualisieren() {
    await client.invalidateQueries({
      queryKey: schluessel.abhaengigkeiten(objekt.art, objekt.id),
    });
  }
  const setzen = useMutation({
    mutationFn: () =>
      api.abhaengigkeiten.setzen.mutate(
        richtung === "braucht"
          ? { voraussetzung: auswahl!, abhaengig: objekt }
          : { voraussetzung: objekt, abhaengig: auswahl! },
      ),
    onSuccess: async () => {
      setzeOffen(false);
      setzeAuswahl(null);
      setzeBegriff("");
      await aktualisieren();
    },
  });
  const entfernen = useMutation({
    mutationFn: (id: string) => api.abhaengigkeiten.entfernen.mutate({ id }),
    onSuccess: aktualisieren,
  });

  const abhaengigkeiten = (liste.data ?? []) as Abhaengigkeit[];
  const voraussetzungen = abhaengigkeiten.filter(
    (eintrag) =>
      eintrag.abhaengig.art === objekt.art &&
      eintrag.abhaengig.id === objekt.id,
  );
  const nachfolger = abhaengigkeiten.filter(
    (eintrag) =>
      eintrag.voraussetzung.art === objekt.art &&
      eintrag.voraussetzung.id === objekt.id,
  );

  return (
    <div
      className={cx(
        kompakt ? "mt-2 border-l-2 border-linie pl-3" : "mt-13",
      )}
      data-testid={`abhaengigkeiten-${objekt.art}-${objekt.id}`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-tinte pb-2.5">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[12px] font-medium text-tinte">
            Abhängigkeiten
          </span>
          <Mikrolabel grad={10.5}>{abhaengigkeiten.length}</Mikrolabel>
        </div>
        {kannAendern ? (
          <button
            type="button"
            className={cx("font-mono text-[11.5px] text-akzent", focusRing)}
            onClick={() => setzeOffen(true)}
          >
            + hinzufügen
          </button>
        ) : null}
      </div>

      {abhaengigkeiten.length === 0 ? (
        <Mikrolabel grad={11} className="block py-3">
          Keine Abhängigkeiten gesetzt.
        </Mikrolabel>
      ) : (
        <div>
          {voraussetzungen.map((eintrag) => (
            <div
              key={eintrag.id}
              className="flex min-h-11 items-center gap-2 border-b border-linie py-2"
            >
              <ArrowLeft className="size-3.5 flex-none text-tinte4" />
              <span
                className={cx(
                  "size-2 flex-none",
                        eintrag.offen ? "bg-warn" : "bg-tinte",
                )}
                title={eintrag.offen ? "blockiert noch" : "erledigt"}
              />
              <PlanobjektVerweis objekt={eintrag.voraussetzung} />
              {kannAendern ? (
                <button
                  type="button"
                  aria-label="Abhängigkeit entfernen"
                  className="p-1 text-tinte4 hover:text-warn"
                  disabled={entfernen.isPending}
                  onClick={() => entfernen.mutate(eintrag.id)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              ) : null}
            </div>
          ))}
          {nachfolger.map((eintrag) => (
            <div
              key={eintrag.id}
              className="flex min-h-11 items-center gap-2 border-b border-linie py-2"
            >
              <ArrowRight className="size-3.5 flex-none text-tinte4" />
              <Link2 className="size-3.5 flex-none text-tinte4" />
              <PlanobjektVerweis objekt={eintrag.abhaengig} />
              {kannAendern ? (
                <button
                  type="button"
                  aria-label="Abhängigkeit entfernen"
                  className="p-1 text-tinte4 hover:text-warn"
                  disabled={entfernen.isPending}
                  onClick={() => entfernen.mutate(eintrag.id)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <Dialog open={offen} onOpenChange={setzeOffen}>
        <DialogContent className="max-h-[90vh] max-w-2xl">
          <DialogHeader>
            <DialogTitle>Abhängigkeit hinzufügen</DialogTitle>
            <DialogDescription>
              Alle Planebenen sind kombinierbar. Zyklen werden serverseitig
              verhindert.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              type="button"
              className={cx(
                "border px-3 py-2 text-left text-sm",
                richtung === "braucht"
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-linie",
              )}
              onClick={() => setzeRichtung("braucht")}
            >
              Dieses Item benötigt …
            </button>
            <button
              type="button"
              className={cx(
                "border px-3 py-2 text-left text-sm",
                richtung === "blockiert"
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-linie",
              )}
              onClick={() => setzeRichtung("blockiert")}
            >
              Dieses Item blockiert …
            </button>
          </div>

          <div className="mt-4">
            <Label htmlFor={`planobjekt-suche-${objekt.id}`}>Planobjekt suchen</Label>
            <Input
              id={`planobjekt-suche-${objekt.id}`}
              className="mt-1"
              value={begriff}
              placeholder="Meilenstein, Arbeitspaket, Aufgabe oder Kriterium …"
              onChange={(ereignis) => {
                setzeBegriff(ereignis.target.value);
                setzeAuswahl(null);
              }}
            />
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto border border-linie">
            {suche.isLoading ? (
              <Mikrolabel grad={11} className="block p-3">
                Suche …
              </Mikrolabel>
            ) : (suche.data?.length ?? 0) === 0 ? (
              <Mikrolabel grad={11} className="block p-3">
                Kein passendes Planobjekt.
              </Mikrolabel>
            ) : (
              (suche.data as KurzesPlanobjekt[]).map((kandidat) => {
                const aktiv =
                  auswahl?.art === kandidat.art && auswahl.id === kandidat.id;
                return (
                  <button
                    key={`${kandidat.art}:${kandidat.id}`}
                    type="button"
                    className={cx(
                      "grid w-full grid-cols-[100px_1fr] gap-3 border-b border-linie px-3 py-2.5 text-left last:border-b-0",
                      aktiv ? "bg-blue-600 text-white" : "hover:bg-wisch",
                    )}
                    onClick={() => setzeAuswahl(kandidat)}
                  >
                    <Mikrolabel
                      grad={10.5}
                      className={aktiv ? "text-white" : undefined}
                    >
                      {ART_LABEL[kandidat.art]}
                    </Mikrolabel>
                    <span className="min-w-0">
                      <span
                        className={cx(
                          "block font-mono text-[10.5px]",
                          aktiv ? "text-white/75" : "text-tinte4",
                        )}
                      >
                        {kandidat.kennung}
                      </span>
                      <span
                        className={cx(
                          "block truncate text-sm",
                          aktiv ? "text-white" : "text-tinte",
                        )}
                      >
                        {kandidat.titel}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {setzen.error ? (
            <p className="mt-3 text-sm text-warn">{setzen.error.message}</p>
          ) : null}
          <DialogFooter className="mt-5">
            <Button type="button" variant="secondary" onClick={() => setzeOffen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={!auswahl || setzen.isPending}
              onClick={() => setzen.mutate()}
            >
              <Plus className="mr-1.5 size-4" />
              Abhängigkeit setzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
